import React from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { Text } from '../components/Texte';
import { StatusBar } from 'expo-status-bar';
import { SafeAreaView } from 'react-native-safe-area-context';
import { ScreenHeader } from '../components/ScreenHeader';
import { colors, screenPadding, spacing, typography } from '../theme/tokens';
import { useGabarit } from '../hooks/useGabarit';
import { t } from '../i18n';
import { ouvrirLien } from '../utils/ouvrirLien';

const CONTACT_EMAIL = 'info@alternjob.com';

// Lien mailto cliquable (Text imbriqué dans Text, autorisé en RN).
function Mail({ testID }: { testID?: string }) {
  return (
    <Text testID={testID} style={styles.link} onPress={() => ouvrirLien(`mailto:${CONTACT_EMAIL}`)}>
      {CONTACT_EMAIL}
    </Text>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>{title}</Text>
      {children}
    </View>
  );
}

function P({ children }: { children: React.ReactNode }) {
  return <Text style={styles.paragraph}>{children}</Text>;
}

function Bullets({ items }: { items: string[] }) {
  return (
    <View style={styles.bullets}>
      {items.map((it, i) => (
        <View key={i} style={styles.bulletRow}>
          <Text style={styles.bulletDot}>•</Text>
          <Text style={styles.bulletText}>{it}</Text>
        </View>
      ))}
    </View>
  );
}

// Item descriptif (remplace un tableau) : intitulé en gras + attributs sur les lignes suivantes.
function DefItem({ title, body }: { title: string; body: string }) {
  return (
    <View style={styles.defItem}>
      <Text style={styles.defTitle}>{title}</Text>
      <Text style={styles.defBody}>{body}</Text>
    </View>
  );
}

// Politique de confidentialité v1.5 (texte validé). onOpenTerms : rend le renvoi aux CGU
// cliquable quand un navigateur le fournit ; sinon le renvoi reste en texte simple.
export function PrivacyScreen({ onBack, onOpenTerms }: { onBack?: () => void; onOpenTerms?: () => void }) {
  const { large } = useGabarit();
  const CguLink = () =>
    onOpenTerms ? (
      <Text style={styles.link} onPress={onOpenTerms}>
        Conditions Générales d’Utilisation
      </Text>
    ) : (
      <Text>Conditions Générales d’Utilisation</Text>
    );

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      {/* Fond clair : icônes foncées. Sur la pile d'avant connexion, cette page s'ouvre par-dessus la
          connexion, dont la barre est claire ; StatusBar empile ses réglages et la rend au retour. */}
      <StatusBar style="dark" />
      <ScreenHeader onBack={onBack} title={t('legalHeaders.privacy')} testID="privacy-back" />
      <ScrollView contentContainerStyle={[styles.content, large && styles.contentL]}>
        <Text style={styles.docTitle}>POLITIQUE DE CONFIDENTIALITÉ - HORMÉIA</Text>
        <Text style={styles.docSubtitle}>Version 1.12 - En vigueur au 28/10/2026</Text>

        <Section title="Préambule">
          <P>
            AlternJob accorde de l’importance à la protection de tes données. Cette politique explique quelles
            données l’application Horméia (« l’Application ») collecte, pourquoi, sur quelle base légale, avec
            qui elles sont partagées, combien de temps elles sont conservées et quels sont tes droits. Elle fait
            partie intégrante des <CguLink />.
          </P>
        </Section>

        <Section title="1. Responsable du traitement">
          <P>
            L’Application est éditée par Alternjob, SAS au capital de 1 000 €, immatriculée au RCS de Nanterre
            sous le n° 892 356 965, dont le siège social est situé au 34 route des Gardes, 92190 Meudon.
          </P>
          <P>Responsable du traitement au sens du RGPD : Alternjob.</P>
          <P>
            Contact données personnelles : <Mail testID="privacy-email-responsable" />.
          </P>
          <P>
            Délégué à la protection des données : Julien DAUZATS, CEO - <Mail />.
          </P>
        </Section>

        <Section title="2. Données que nous collectons">
          <Text style={styles.subTitle}>2.1 Données de compte et de profil</Text>
          <Bullets
            items={[
              'Adresse email (obligatoire).',
              'Nom, prénom (facultatif).',
              'Numéro de téléphone, adresse postale, ville (facultatif).',
              'Formation, établissement, profil LinkedIn, statut de recherche (stage, alternance, contrat).',
              'Date de naissance (facultative), pour connaître la tranche d’âge de nos utilisateurs, adapter le ton des lettres préparées et, si tu as moins de 18 ans, te signaler les offres encadrées pour les mineurs (§ 9).',
              'Photo de profil (facultatif).',
              'Niveau d’études et ville, déduits par l’IA à partir de ton CV lorsque tu le fais analyser, puis enregistrés dans ton profil.',
              'Si tu utilises la connexion Google ou Apple : les informations d’identité transmises par ces services (identifiant, email, nom).',
            ]}
          />
          <Text style={styles.subTitle}>2.2 Contenus que tu crées ou importes</Text>
          <Bullets
            items={[
              'CV : texte saisi et fichiers importés (PDF, Word).',
              'Contenu de ton profil LinkedIn (capture que tu transmets).',
              'Fiches de poste que tu importes.',
              'Réponses aux simulations d’entretien (texte saisi ou dicté).',
              'Lettres de motivation que tu génères, analyses ou optimises, y compris celles que tu importes au format PDF ou Word, et les paramètres que tu fournis pour les produire (type de candidature, ton, langue, métier et organisation visés).',
              'Annonces et descriptions d’opportunité que tu copies ou importes pour une lettre.',
              'Coordonnées du destinataire d’une lettre lorsque tu les renseignes : nom et fonction. Ces informations concernent un tiers ; nous les conservons uniquement au sein de la lettre à laquelle elles se rapportent, et elles disparaissent avec elle.',
              'Tes critères de recherche d’offres : métier, ville et rayon, types de contrat, rythme d’alternance, permis B, date de disponibilité.',
              'Tes décisions sur les offres (passée, intéressée, sauvegardée) et une copie des offres que tu sauvegardes, pour les retrouver.',
              'Tes dossiers de candidature préparés pour une offre : analyse de l’offre, CV adapté, lettre, réponses aux questions de l’offre, points de contrôle, et les corrections que tu y apportes.',
            ]}
          />
          <P>
            Les offres affichées proviennent de France Travail, de La bonne alternance et d’Adzuna (mention
            « Jobs by Adzuna »), selon leurs conditions de réutilisation ; chaque offre indique sa source et sa
            date de publication ou de mise à jour. Pour les obtenir, nos serveurs interrogent ces services avec
            tes critères de recherche, sans aucune donnée d’identité. À Adzuna, nous transmettons uniquement
            les mots-clés du métier que tu vises, le nom de ta commune, le rayon de recherche et le type de
            contrat recherché : ni ton nom, ni ton email, ni ton identifiant. Une recherche est mise en cache
            12 heures au plus.
          </P>
          <P>
            Adzuna ne transmet qu’un extrait de chaque annonce. En ouvrant l’offre complète ou la mention
            « Jobs by Adzuna », tu es redirigé vers le site adzuna.fr, soumis à la politique de confidentialité
            d’Adzuna ; nous ne contrôlons pas les données que ce site collecte.
          </P>
          <P>
            Connexion LinkedIn (facultative) : si tu autorises l’accès à ton profil via LinkedIn
            (consentement OAuth), nous récupérons uniquement, pour réaliser l’audit, les données
            suivantes : titre du profil (headline), résumé, secteur d’activité, expériences
            professionnelles (intitulé, entreprise, description, lieu, dates) et compétences.
          </P>
          <P>
            Nous ne conservons ni n’exploitons jamais, bien que l’API LinkedIn le permette : ta date de
            naissance, ton adresse, ton code postal, ta géolocalisation, ton nom et ton prénom, tes
            messages et tes contacts.
          </P>
          <P>
            Résultat de ta dernière notation de CV : nous conservons le score ATS obtenu, sa date et les
            conseils d’amélioration produits par l’IA, afin de te les réafficher sans relancer une analyse
            ni décompter ton quota. Seul le dernier résultat est conservé - une nouvelle notation remplace
            le précédent.
          </P>
          <P>
            CV importé et enregistré : si tu demandes à enregistrer un CV que tu as importé, l’IA en
            transpose le contenu dans le format de l’Application, sans le réécrire ni l’enrichir. Le
            résultat est conservé comme un CV que tu aurais créé ici, et tu peux le supprimer à tout
            moment.
          </P>
          <Text style={styles.subTitle}>2.3 Données d’usage</Text>
          <Bullets
            items={[
              'Compteurs d’usage : nombre de CV créés, de notations, d’entretiens réalisés, afin de gérer les quotas liés à ton offre.',
              'Ouvertures de l’Application : au plus une par jour, sans aucun contenu ni donnée de localisation, à des fins de mesure de fréquentation.',
              'Mesure d’usage, côté serveur : quand tu utilises la notation, la création ou l’amélioration de CV, les lettres de motivation, l’audit LinkedIn, la simulation d’entretien (voix de l’intervieweur comprise) ou l’onglet Offres (recherche d’offres, préparation d’un dossier de candidature), nos serveurs enregistrent les étapes de ton parcours : début, étapes franchies, aboutissement ou erreur (avec un code technique choisi dans une liste fermée), leur date, leur durée, le nombre d’unités de texte (jetons) traitées par l’IA, la durée de la voix de synthèse, le modèle d’IA utilisé et une estimation du coût correspondant. Jamais le contenu : ni ton CV, ni tes lettres, ni tes réponses, ni l’annonce, ni tes critères de recherche.',
              'Mesure d’usage, côté Application : dans l’onglet Offres, l’Application enregistre l’affichage d’une offre à l’écran pendant au moins une seconde, l’ouverture de son détail, le clic vers l’annonce d’origine et le fait que tu as pris une décision sur l’offre, avec l’identifiant de l’offre, l’emplacement dans l’écran, la version de l’Application et le type d’appareil (iOS, Android ou web). Un clic vers l’annonce d’origine n’est jamais enregistré comme une candidature.',
              'Adresse IP : transmise lorsque l’Application interroge l’API publique du site alternjob.com pour afficher les contenus éditoriaux de l’accueil. Nous ne la conservons pas ; elle est reçue par Cloudflare, qui héberge ce site (§ 5).',
                'Adresse IP : également transmise lorsque l’Application vérifie, à son lancement, si une mise à jour de son contenu est disponible. Cette vérification ne transmet aucune donnée de compte : seuls la plateforme, le canal de distribution et un identifiant technique de version sont envoyés (§ 5).',
              'Retours que tu nous envoies : ton avis sur un résultat et le commentaire libre que tu choisis d’écrire (2 000 caractères au plus).',
              'Adresse IP : également transmise à l’API publique geo.api.gouv.fr, service de l’État, lorsque tu saisis une commune pour en vérifier le nom (§ 5).',
            ]}
          />
          <P>
            Mesure d’usage : ces événements sont rattachés à ton compte par son identifiant interne ; ils ne
            contiennent ni ton nom, ni ton email, ni aucun texte que tu as écrit ou importé. Ils servent à piloter
            l’Application : repérer les étapes où les utilisateurs rencontrent des difficultés ou des erreurs,
            mesurer les temps de réponse et le coût de l’IA, et vérifier que l’Application aide réellement à
            progresser. Ils sont hébergés par Supabase dans l’Union européenne (§ 5), consultables uniquement par
            les administrateurs habilités d’Alternjob, et ne sont transmis à aucun tiers, ni établissement
            partenaire, ni annonceur. Ils ne servent à aucune publicité et à aucune décision te concernant (§ 10).
            Tu peux t’y opposer (§ 8).
          </P>
          <P>
            D’autres écrans de l’Application pourront à l’avenir enregistrer des événements de même nature,
            toujours sans aucun contenu. Si une nouvelle catégorie de données devait être collectée, la présente
            politique serait mise à jour au préalable (§ 12).
          </P>
          <P>
            Prévention du contournement des quotas : lorsqu’un compte est supprimé, nous conservons une
            empreinte cryptographique irréversible de l’adresse email associée, accompagnée du nombre
            d’analyses déjà effectuées pendant le mois en cours. Cette empreinte ne permet pas de retrouver
            l’adresse ; elle sert uniquement à vérifier, si un compte est recréé avec la même adresse avant
            la fin du mois, que le décompte reprend là où il s’était arrêté. Aucune adresse email, aucun nom
            et aucun identifiant de compte ne sont conservés.
          </P>
          <Text style={styles.subTitle}>2.4 Données de facturation</Text>
          <P>
            Si tu souscris une offre payante : nom, email, message associé à la réservation, historique des
            transactions. Tes coordonnées bancaires ne transitent jamais par nos serveurs (voir § 5.3).
          </P>
          <Text style={styles.subTitle}>2.5 Ce que nous ne collectons pas</Text>
          <Bullets
            items={[
              'Aucune géolocalisation de ton appareil : seule la commune que tu choisis pour la recherche d’offres, avec ses coordonnées, est enregistrée dans tes critères.',
              'Aucun outil publicitaire ni traceur tiers à des fins de ciblage.',
              'Aucune revente de données.',
            ]}
          />
          <Text style={styles.subTitle}>2.6 Déduction de l’établissement de rattachement</Text>
          <P>
            À partir du domaine de ton adresse email (ex. @etu.univ-xxx.fr), nous pouvons déduire ton
            établissement de rattachement, afin d’établir des statistiques d’usage agrégées par établissement et,
            le cas échéant, d’alimenter le reporting mis à disposition des établissements partenaires. Cette
            déduction ne produit aucun effet juridique à ton égard et ne constitue pas une décision automatisée.
            Tu peux t’y opposer à tout moment (§ 8).
          </P>
        </Section>

        <Section title="3. Données sensibles : point d’attention important">
          <P>
            Un CV, un profil LinkedIn ou une réponse d’entretien peuvent contenir, à ton initiative, des données
            dites sensibles au sens de l’article 9 du RGPD : état de santé, situation de handicap ou RQTH,
            origine, opinions politiques ou religieuses, appartenance syndicale, orientation sexuelle.
          </P>
          <P>
            Nous ne sollicitons jamais ces données et n’en avons aucun usage. Si tu choisis d’en faire figurer
            dans un document que tu importes, tu consens expressément à ce qu’elles soient traitées dans le cadre
            strict de l’analyse de ce document (art. 9.2.a du RGPD). Tu peux retirer ce consentement à tout
            moment en supprimant le document concerné.
          </P>
          <P>
            Nous te recommandons de retirer de tes documents toute information sensible que tu ne souhaites pas
            voir traitée.
          </P>
        </Section>

        <Section title="4. Finalités et bases légales">
          <DefItem
            title="Création et gestion du compte"
            body="Te permettre d’accéder au service. Base légale : exécution du contrat (6.1.b). Conservation : durée du compte."
          />
          <DefItem
            title="Âge et adaptation du service"
            body="Connaître la tranche d’âge de nos utilisateurs, adapter le ton des lettres et signaler aux mineurs les offres encadrées pour eux (§ 9). La date de naissance est facultative. Base légale : intérêt légitime (6.1.f). Conservation : durée du compte."
          />
          <DefItem
            title="Notation, création et import de CV, audit LinkedIn, simulation d’entretien, génération, analyse et optimisation de lettres de motivation"
            body="Fournir les fonctionnalités demandées. Base légale : exécution du contrat (6.1.b). Conservation : voir § 6."
          />
          <DefItem
            title="Recherche d’offres, score de correspondance et préparation de candidatures (onglet Offres)"
            body="Afficher des offres adaptées à tes critères, calculer un score de correspondance par des règles fixes (sans IA), et préparer avec l’IA un CV adapté et une lettre pour une offre. Base légale : exécution du contrat (6.1.b). Conservation : voir § 6."
          />
          <DefItem
            title="Connexion LinkedIn"
            body="Analyser ton profil LinkedIn et te proposer des améliorations. Base légale : consentement (6.1.a), que tu donnes explicitement en autorisant l’accès via LinkedIn (OAuth). Conservation : jusqu’à la déconnexion LinkedIn ou la suppression du compte."
          />
          <DefItem
            title="Traitement des données sensibles éventuellement présentes dans les CV"
            body="Analyser le document que tu transmets. Base légale : consentement explicite (9.2.a). Conservation : jusqu’à suppression du document."
          />
          <DefItem
            title="Compteurs d’usage et quotas"
            body="Appliquer les limites liées à ton offre. Base légale : exécution du contrat (6.1.b). Conservation : durée du compte."
          />
          <DefItem
            title="Mesure de fréquentation (ouvertures)"
            body="Comprendre l’audience et améliorer le service. Base légale : intérêt légitime (6.1.f). Conservation : durée du compte."
          />
          <DefItem
            title="Mesure d’usage et pilotage de l’Application"
            body="Comprendre comment l’Application est utilisée, repérer les difficultés et les erreurs, mesurer les temps de réponse et le coût de l’IA, et suivre les progrès des utilisateurs dans leurs parcours, afin d’améliorer le service. Données rattachées à ton compte, sans aucun contenu (§ 2.3). Base légale : intérêt légitime (6.1.f) ; tu peux t’y opposer (§ 8). Conservation : 12 mois à compter de chaque événement ; effacement dès la suppression de ton compte."
          />
          <DefItem
            title="Retours sur les résultats"
            body="Améliorer la qualité des analyses à partir de ton avis et de ton commentaire. Base légale : intérêt légitime (6.1.f). Conservation : durée du compte."
          />
          <DefItem
            title="Affichage des contenus éditoriaux d’alternjob.com"
            body="Te proposer les articles et podcasts publiés sur notre site. Base légale : intérêt légitime (6.1.f). Donnée concernée : adresse IP, transmise à l’hébergement du site lors de l’appel. Conservation : aucune de notre fait."
          />
          <DefItem
            title="Paiement et facturation"
            body="Traiter les transactions. Base légale : contrat et obligation légale (6.1.c). Conservation : 10 ans (art. L123-22 C. com.)."
          />
          <DefItem
            title="Sécurité, prévention de la fraude et des abus"
            body="Protéger le service et les utilisateurs. Base légale : intérêt légitime (6.1.f). Conservation : 12 mois pour les journaux d’usage de l’IA ; journaux d’erreurs : durée du compte."
          />
          <DefItem
            title="Prévention du contournement des quotas gratuits"
            body="Empêcher qu’un compte supprimé puis recréé avec la même adresse ne réinitialise les limites mensuelles de l’offre gratuite. Base légale : intérêt légitime (6.1.f). Conservation : jusqu’à la fin du mois concerné, un mois au maximum."
          />
          <DefItem
            title="Communications relatives au service (email transactionnel)"
            body="T’informer d’une évolution ou d’un incident. Base légale : exécution du contrat (6.1.b). Conservation : durée du compte."
          />
          <DefItem
            title="Gestion des demandes d’exercice de droits"
            body="Répondre à tes demandes. Base légale : obligation légale (6.1.c). Conservation : 1 an après clôture ; toute pièce d’identité éventuellement transmise est supprimée sans délai après vérification."
          />
        </Section>

        <Section title="5. Destinataires et sous-traitants">
          <P>
            Tes données ne sont partagées qu’avec les prestataires strictement nécessaires au fonctionnement du
            service. Chacun agit en qualité de sous-traitant, dans le cadre défini à l’article 28 du
            RGPD, et n’est pas autorisé à utiliser tes données à ses propres fins. Les modalités propres à
            chaque prestataire sont précisées ci-dessous.
          </P>
          <DefItem
            title="Supabase"
            body="Hébergement, base de données, stockage de fichiers. Données concernées : compte, contenus, fichiers. Localisation : Union européenne. Encadrement : DPA."
          />
          <DefItem
            title="Resend"
            body="Envoi des emails transactionnels (confirmation de compte, réinitialisation de mot de passe). Données concernées : adresse email. Localisation : Union européenne (région Irlande). Encadrement : DPA."
          />
          <DefItem
            title="Anthropic"
            body="Fourniture du modèle d’IA Claude (analyse). Données concernées : contenu des CV, profils, fiches de poste, réponses d’entretien ; offres d’emploi, sans les coordonnées du recruteur ; pour la préparation d’une candidature, ton CV et tes réponses clés (permis, disponibilité, rythme) - jamais ton nom, ton téléphone ni ton email. Localisation : États-Unis. Encadrement : DPA et clauses contractuelles types (art. 46 RGPD)."
          />
          <DefItem
            title="Stripe"
            body="Paiement. Données concernées : nom, email, message, données de transaction. Localisation : UE / États-Unis. Encadrement : DPA et garanties appropriées."
          />
          <DefItem
            title="RevenueCat"
            body="Gestion des abonnements souscrits dans l’Application. Données concernées : identifiant de compte, statut et historique d’abonnement. Localisation : États-Unis."
          />
          <DefItem
            title="Cal.com"
            body="Prise de rendez-vous de coaching. Données concernées : nom, adresse email, créneau choisi. Localisation : États-Unis."
          />
          <DefItem
            title="OVH"
            body="Hébergement de la messagerie associée au domaine alternjob.com. Données concernées : adresse email et contenu des messages que tu nous adresses. Localisation : France (Union européenne)."
          />
          <DefItem
            title="Cloudflare"
            body="Hébergement du site alternjob.com et de son API publique, que l’Application interroge pour afficher les contenus éditoriaux, ainsi que la résolution DNS du domaine. Données concernées : adresse IP, données de connexion. Localisation : réseau mondial de Cloudflare ; société établie aux États-Unis."
          />
          <DefItem
            title="Expo (650 Industries, Inc.)"
            body="Distribution des mises à jour de contenu de l’Application - corrections, textes, images - sans passage par les magasins d’applications. Données concernées : adresse IP, plateforme, canal de distribution, identifiant technique de version. Aucune donnée de compte, aucun contenu que tu as créé. Localisation : États-Unis."
          />
          <DefItem
            title="Sentry (Functional Software, Inc.)"
            body="Détection et diagnostic des pannes de l’Application. Données concernées : type et pile d’appel des erreurs, écran concerné, plateforme, version de l’Application et identifiant technique de mise à jour. Aucune donnée de compte, aucun contenu que tu as créé, aucune adresse IP conservée. Localisation : Union européenne (région Allemagne). Éditeur établi aux États-Unis."
          />
          <DefItem
            title="ElevenLabs"
            body="Voix de synthèse de l’intervieweur pendant les simulations d’entretien. Données concernées : le texte des questions de l’intervieweur, rédigé par notre IA, qui peut reprendre des éléments de ton CV ou de tes réponses. Ta voix ne lui est jamais transmise, ni ton adresse email, ni ton identifiant de compte. ElevenLabs Poland sp. z o.o. (Pologne), filiale d’Eleven Labs Inc. (États-Unis). Les données sont stockées aux États-Unis, le transfert est encadré par des clauses contractuelles types, et elles sont conservées selon la politique d’ElevenLabs."
          />
          <DefItem
            title="France Travail, La bonne alternance, Adzuna"
            body="Fourniture des offres d’emploi. Données concernées : métier visé (codes ROME ou mots-clés), commune ou ses coordonnées, rayon de recherche, type de contrat. Jamais ton nom, ton email ni ton identifiant (§ 2.2)."
          />
          <DefItem
            title="API Géo (geo.api.gouv.fr)"
            body="Vérification du nom des communes saisies. Données concernées : nom de la commune saisie, adresse IP. Service de l’État français."
          />
          <DefItem
            title="Google / Apple"
            body="Authentification (si tu l’utilises) et reconnaissance vocale native. Données concernées : identifiants de connexion ; audio dicté, traité par Apple ou Google selon ton appareil. Encadrement : selon leurs politiques respectives."
          />
          <P>
            Autres cas de communication : nous pouvons être tenus de transmettre des données à une autorité
            administrative ou judiciaire sur réquisition légale.
          </P>

          <Text style={styles.subTitle}>5.1 Analyse par intelligence artificielle</Text>
          <P>
            Pour analyser tes CV, ton profil LinkedIn, tes entretiens et tes lettres de motivation, nous transmettons le contenu concerné à
            Anthropic, fournisseur du modèle Claude, via son API commerciale. Selon les conditions commerciales
            d’Anthropic : tes données ne sont pas utilisées pour entraîner les modèles ; elles sont supprimées
            des serveurs d’Anthropic sous 30 jours, sauf conservation plus longue imposée par la loi ou par un
            signalement de sécurité.
          </P>
          <P>
            Les fichiers que tu importes (CV, lettres de motivation, captures LinkedIn, fiches de poste) sont transmis pour analyse mais
            ne sont pas conservés par l’Application : seul le CV structuré que tu génères est enregistré dans ton
            compte.
          </P>
          <P>
            Ce transfert s’effectue vers les États-Unis, encadré par les clauses contractuelles types de la
            Commission européenne et les mesures complémentaires prévues au contrat conclu avec Anthropic.
          </P>

          <Text style={styles.subTitle}>5.2 Voix : saisie vocale et voix de l’intervieweur</Text>
          <P>
            Si tu utilises la saisie vocale lors des simulations d’entretien, l’audio est traité par le service
            de reconnaissance vocale de ton appareil (Apple ou Google). Nous ne conservons aucun enregistrement
            audio : seul le texte transcrit est utilisé, exactement comme une réponse saisie au clavier.
          </P>
          <P>
            Pendant les simulations d’entretien, les questions de l’intervieweur peuvent être lues à voix haute
            par une voix de synthèse fournie par ElevenLabs. Nous lui transmettons uniquement le texte de ces
            questions, rédigé par notre IA, qui peut reprendre des éléments de ton CV ou de tes réponses. Ta voix
            ne lui est jamais transmise, ni ton adresse email, ni ton identifiant de compte. Tu peux couper le son
            de l’intervieweur à tout moment : plus rien n’est alors transmis à ElevenLabs. Prestataire :
            ElevenLabs Poland sp. z o.o. (Pologne), filiale d’Eleven Labs Inc. (États-Unis). Les données sont stockées aux États-Unis, le transfert est encadré par des clauses contractuelles types, et elles sont conservées selon la politique d’ElevenLabs.
          </P>

          <Text style={styles.subTitle}>5.3 Paiements</Text>
          <P>
            Les séances de coaching sont payées via Stripe. Tes données de carte bancaire sont saisies directement
            dans l’interface sécurisée de Stripe et ne transitent jamais par nos serveurs. Nous transmettons à
            Stripe ton nom, ton email et le message associé à ta réservation.
          </P>
          <P>
            Les abonnements souscrits dans l’Application sont facturés par la plateforme depuis laquelle tu l’as
            installée, Apple ou Google, selon ses propres conditions. Nous n’avons accès ni à tes coordonnées
            bancaires ni à ton moyen de paiement : nous ne recevons, par l’intermédiaire de RevenueCat, que le
            statut et l’échéance de ton abonnement.
          </P>

          <Text style={styles.subTitle}>5.4 Source de données tierce : LinkedIn</Text>
          <P>
            LinkedIn n’est pas notre sous-traitant : c’est un service tiers dont tu nous autorises à lire une
            partie des données via ton consentement OAuth, dans le seul but de réaliser l’audit de ton profil.
            LinkedIn agit comme responsable de traitement pour son propre service, selon sa propre politique de
            confidentialité. Nous ne recevons de LinkedIn que les données listées au § 2.2, et tu peux retirer
            cette autorisation à tout moment (§ 8).
          </P>
        </Section>

        <Section title="6. Durées de conservation">
          <Bullets
            items={[
              'Compte actif : tes données sont conservées tant que ton compte est actif.',
              'Suppression volontaire : tu peux supprimer tes CV et tes photos à tout moment depuis l’Application. Quand tu supprimes ton compte, l’accès cesse immédiatement : ta session est fermée, tes données ne sont plus lisibles ni utilisables, et ton adresse email est libérée sur-le-champ - tu peux recréer un compte avec la même adresse sans attendre.',
              'Dernière notation de CV : conservée jusqu’à la notation suivante, qui la remplace, et au plus tard jusqu’à la suppression de ton compte.',
              'Archive de restauration : pendant 29 jours, nous conservons une copie de tes données de compte (profil, CV, abonnement) dans un espace d’archive isolé, inaccessible depuis l’Application. Cette copie sert uniquement à réparer une suppression accidentelle ou erronée, à ta demande ; elle ne peut être réactivée que manuellement par nos soins et n’est communiquée à personne. Passé ce délai, elle est détruite automatiquement et définitivement.',
              'Effacement immédiat, sans archive ni restauration possible : ton autorisation LinkedIn est révoquée auprès de LinkedIn ; si tu t’étais connecté avec Apple et que tu supprimes ton compte depuis l’Application sur iPhone, nous demandons aussi à Apple de révoquer l’autorisation de connexion accordée à Horméia ; tes photos importées sont effacées, et les données personnelles de tes demandes de coaching sont anonymisées - seules les informations comptables obligatoires sont conservées, détachées de ton identité.',
              'Abonnement : notre enregistrement de ton abonnement suit le sort de ton compte, archive de restauration comprise. RevenueCat, qui gère les abonnements pour notre compte, conserve sa propre fiche client selon sa politique ; l’historique d’achat reste par ailleurs attaché à ton compte Apple ou Google.',
              'Empreinte de prévention des quotas : conservée jusqu’à la fin du mois de la suppression, un mois au maximum, puis effacée automatiquement.',
              'Fichiers importés : le fichier lui-même (PDF, Word) n’est jamais conservé au-delà de l’analyse. Si tu choisis d’enregistrer un CV importé dans « Mes CV », c’est son contenu structuré qui est conservé, comme pour un CV créé dans l’Application, jusqu’à ce que tu le supprimes.',
              'Enregistrements audio : non conservés.',
              'Lettres de motivation : conservées tant que ton compte est actif, supprimables une par une depuis l’Application, et effacées avec le compte.',
              'Résultat de l’analyse d’une lettre : le score, le diagnostic et les conseils produits sont conservés avec la lettre à laquelle ils se rapportent, et disparaissent avec elle.',
              'Critères de recherche et décisions sur les offres : tant que ton compte est actif, effacés avec lui.',
              'Dossiers de candidature : tant que ton compte est actif, effacés avec lui ; une nouvelle préparation pour la même offre remplace la précédente.',
              'Jeton d’accès LinkedIn : conservé chiffré jusqu’à la déconnexion (que tu peux effectuer à tout moment depuis l’écran d’audit) ou la suppression du compte.',
              'Facturation : conservée 10 ans en archivage à accès restreint (obligation comptable).',
              'Journaux d’usage de l’IA et métriques d’analyse : 12 mois.',
              'Mesure d’usage (événements de parcours) : 12 mois à compter de chaque événement, puis effacement automatique ; effacée dès la suppression de ton compte, sans copie dans l’archive de restauration. Les événements en attente d’envoi sur ton appareil y restent 7 jours au plus et sont effacés dès leur envoi, à la déconnexion ou à la suppression du compte.',
              'Ouvertures de l’Application, retours et journaux d’erreurs : tant que ton compte existe ; à la suppression du compte, les ouvertures et les retours sont effacés et les journaux d’erreurs sont détachés de ton identité.',
            ]}
          />
        </Section>

        <Section title="7. Hébergement et sécurité">
          <P>Tes données sont hébergées par Supabase au sein de l’Union européenne.</P>
          <P>
            L’affichage des contenus éditoriaux de l’accueil s’effectue par un appel direct à l’API publique du
            site alternjob.com. Cet appel transmet ton adresse IP à Cloudflare, qui héberge ce site (§ 5). Aucune
            donnée de compte n’est transmise lors de cet appel.
          </P>
          <P>
            Les données de diagnostic transmises à Sentry (§ 5) sont hébergées au sein de l’Union européenne,
            en Allemagne. Les adresses IP n’y sont pas conservées, leur conservation étant désactivée dans la
            configuration du projet.
          </P>
          {/*
            DPA EXPO NON ENCORE VÉRIFIÉ — la phrase ci-dessus s'arrête volontairement à « vers les
            États-Unis ». Elle est EXACTE en l'état : elle constate un transfert sans prétendre à un
            instrument juridique. Affirmer un encadrement non signé serait la ligne qui se retourne
            contre nous en contrôle.

            Une fois le DPA d'Expo vérifié et accepté, compléter la phrase avec l'UNE des deux
            variantes — jamais les deux, et jamais au hasard : nommer le mauvais instrument est
            aussi risqué que n'en nommer aucun.

              • Si le DPA annexe les clauses contractuelles types :
                « , encadré par les clauses contractuelles types de la Commission européenne
                  prévues au contrat de sous-traitance conclu avec Expo. »

              • Si Expo est certifié au Data Privacy Framework UE–États-Unis :
                « , encadré par la certification d'Expo au Data Privacy Framework UE–États-Unis. »

            Et reporter le choix dans l'historique des versions (§ 13).
          */}
          <P>
            À son lancement, l’Application interroge les serveurs d’Expo pour savoir si une mise à jour de son
            contenu est disponible (§ 5). Cet appel transmet ton adresse IP, la plateforme et un identifiant
            technique de version, à l’exclusion de toute donnée de compte. Ce transfert s’effectue vers les
            États-Unis.
          </P>
          <P>Mesures de sécurité mises en œuvre :</P>
          <Bullets
            items={[
              'Chiffrement en transit (HTTPS/TLS) et au repos.',
              'Cloisonnement strict des accès : chaque utilisateur n’accède qu’à ses propres informations (règles de sécurité au niveau des lignes, Row Level Security).',
              'Accès interne limité aux personnes habilitées, sur la base du besoin d’en connaître.',
              'Journalisation des accès et des opérations sensibles.',
              'Sauvegardes chiffrées.',
            ]}
          />
          <P>
            Photos de profil : ces images sont stockées via des liens accessibles publiquement par
            leur URL. N’y fais figurer aucune information sensible.
          </P>
          <P>
            Violation de données : en cas de violation susceptible d’engendrer un risque pour tes droits et
            libertés, nous notifierons la CNIL sous 72 heures (art. 33 RGPD) et t’informerons individuellement
            dans les meilleurs délais si le risque est élevé (art. 34).
          </P>
        </Section>

        <Section title="8. Tes droits">
          <P>Conformément au RGPD, tu disposes des droits suivants :</P>
          <Bullets
            items={[
              'Accès : obtenir une copie des données que nous détenons sur toi.',
              'Rectification : corriger une donnée inexacte ou incomplète.',
              'Effacement : demander la suppression de tes données ou de ton compte.',
              'Limitation : geler temporairement un traitement contesté.',
              'Portabilité : récupérer tes données dans un format structuré et lisible par machine.',
              'Opposition : t’opposer à un traitement fondé sur notre intérêt légitime.',
              'Retrait du consentement : à tout moment, sans effet rétroactif (données sensibles).',
              'Directives post-mortem : définir le sort de tes données après ton décès.',
            ]}
          />
          <P>
            Retrait du consentement LinkedIn : tu peux révoquer à tout moment l’autorisation d’accès à ton profil
            LinkedIn via le bouton « Déconnecter » de l’écran d’audit, ce qui supprime le jeton d’accès conservé.
          </P>
          <P>
            Opposition à la mesure d’usage : tu peux t’opposer à tout moment à la mesure d’usage décrite au
            § 2.3 en nous écrivant à <Mail />. Nous te répondons dans le délai indiqué ci-dessous, en te disant
            la suite donnée à ta demande. Ton opposition ne change rien à ton accès à l’Application ni à ton
            offre. Dans tous les cas, la suppression de ton compte efface ces événements (§ 6).
          </P>
          <P>
            Comment les exercer : écris-nous à <Mail testID="privacy-email-droits" />. Nous répondons dans un
            délai d’un mois (prorogeable de deux mois en cas de complexité). Une pièce d’identité pourra être
            demandée en cas de doute raisonnable sur ton identité ; elle sera supprimée immédiatement après
            vérification.
          </P>
          <P>
            Réclamation : tu peux introduire une réclamation auprès de la CNIL - 3 place de Fontenoy, TSA 80715,
            75334 Paris Cedex 07 - www.cnil.fr.
          </P>
        </Section>

        <Section title="9. Mineurs">
          <P>
            L’Application est réservée aux personnes âgées de 15 ans au minimum. Si tu as moins de 18 ans, tu dois
            avoir obtenu l’autorisation du ou des titulaires de l’autorité parentale. Si nous apprenons qu’un
            compte a été créé par une personne de moins de 15 ans, il sera supprimé et les données effacées sans
            délai. Un titulaire de l’autorité parentale peut nous saisir à <Mail />.
          </P>
          <P>
            Ta date de naissance sert aussi à adapter le ton des lettres préparées et, si tu as moins de 18 ans,
            à te signaler les offres qui semblent comporter un travail interdit ou encadré pour les mineurs
            (travail de nuit, service d’alcool, travail en hauteur). Ce signalement est indicatif : il ne
            remplace pas la vérification auprès de l’employeur.
          </P>
          <P>
            La mesure d’usage (§ 2.3) s’applique de la même manière si tu as entre 15 et 18 ans : elle ne porte
            sur aucun contenu que tu écris, ne sert à aucune publicité ni à aucune décision te concernant, et tu
            peux t’y opposer à tout moment (§ 8).
          </P>
        </Section>

        <Section title="10. Décisions automatisées et profilage">
          <P>
            Les scores, analyses et recommandations générés par l’IA sont purement indicatifs. Ils ne sont
            jamais transmis automatiquement à un employeur ou à un recruteur, et ne produisent aucun effet
            juridique ni aucun effet significatif à ton égard au sens de l’article 22 du RGPD. Tu peux à tout
            moment ignorer, corriger ou supprimer tout contenu généré par l’IA.
          </P>
          <P>
            Le score de correspondance d’une offre est calculé par des règles fixes à partir de tes critères ;
            il est indicatif et ne masque aucune offre. Aucun dossier de candidature n’est transmis à un
            employeur sans ton action.
          </P>
          <P>
            La mesure d’usage (§ 2.3) sert à améliorer l’Application dans son ensemble. Elle ne produit aucune
            décision te concernant : elle ne modifie ni ton offre, ni tes quotas, ni les offres qui te sont
            proposées, ni leur score de correspondance.
          </P>
        </Section>

        <Section title="11. Cookies et traceurs">
          <P>
            L’Application n’utilise aucun cookie publicitaire ni traceur tiers, et ne lit aucun identifiant
            publicitaire de ton appareil. Elle utilise des identifiants techniques nécessaires à son
            fonctionnement (session, authentification), exemptés de consentement.
          </P>
          <P>
            Pour la mesure d’usage (§ 2.3), l’Application conserve temporairement sur ton appareil, ou dans le
            stockage de ton navigateur pour la version web, les événements qui n’ont pas encore pu être envoyés,
            par exemple en l’absence de réseau : 7 jours au plus, effacés dès leur envoi, à la déconnexion ou à
            la suppression de ton compte. Les événements d’un même lancement sont regroupés par un identifiant
            de séance tiré au hasard, gardé en mémoire et jamais enregistré sur ton appareil.
          </P>
        </Section>

        <Section title="12. Modification de la présente politique">
          <P>
            Toute modification substantielle te sera notifiée par email et/ou notification in-app. La date
            de dernière mise à jour figure ci-dessous.
          </P>
          <P>
            Dernière mise à jour : 28/10/2026 - Contact : <Mail />.
          </P>
        </Section>

        <Section title="13. Historique des versions">
          <Bullets
            items={[
              'Version 1.12 - 28/10/2026 : nouvelle finalité de mesure d’usage et de pilotage de l’Application, rattachée au compte et sans aucun contenu (§ 2.3, § 4, § 6) ; conservation temporaire des événements non envoyés sur l’appareil et identifiant de séance en mémoire (§ 11) ; droit d’opposition à cette mesure (§ 8) ; précisions pour les 15 à 17 ans (§ 9) et sur l’absence de décision automatisée (§ 10).',
              'Version 1.11 - 24/10/2026 : retrait de la mention d’un double facteur d’authentification et de la vérification de l’âge à l’inscription, qui n’existent pas dans l’Application ; date de naissance décrite comme facultative ; durées de conservation alignées sur celles réellement appliquées (mesure de fréquentation, retours, journaux, comptes inactifs) ; déclaration des retours, du niveau d’études et de la ville déduits du CV, de France Travail, La bonne alternance, Adzuna et de l’API Géo de l’État ; précision sur la localisation.',
              'Version 1.10 - 12/10/2026 : archive de restauration de 29 jours (et non 30), durée réellement appliquée ; reconnaissance vocale de la dictée décrite comme assurée par Apple ou Google selon l’appareil, l’audio pouvant être traité par leurs serveurs ; déclaration d’ElevenLabs parmi les destinataires, pour la voix de l’intervieweur ; abonnement : précision sur la fiche client conservée par RevenueCat ; révocation de l’autorisation Apple lors de la suppression d’un compte connecté avec Apple.',
              'Version 1.9 - 10/10/2026 : onglet Offres - critères de recherche, décisions sur les offres et dossiers de candidature conservés ; offres transmises à Anthropic sans les coordonnées du recruteur ; sources France Travail, La bonne alternance et Adzuna ; redirection vers adzuna.fr pour l’annonce complète d’une offre Adzuna.',
              'Version 1.8 - 24/09/2026 : adresse complète du siège de l’éditeur, dans la présente politique et dans les conditions générales d’utilisation ; aucun autre changement.',
              'Version 1.7 - 17/09/2026 : déclaration de Sentry parmi les destinataires, pour la détection des pannes de l’Application.',
              'Version 1.6 - 08/09/2026 : déclaration d’Expo (650 Industries) parmi les destinataires, pour la distribution des mises à jour de contenu de l’Application ; mention de la transmission de l’adresse IP vers les États-Unis à cette occasion.',
              'Version 1.5 - 16/08/2026 : déclaration de RevenueCat, Cal.com, OVH et Cloudflare parmi les destinataires ; mention de la transmission de l’adresse IP lors de la lecture des contenus éditoriaux d’alternjob.com.',
              'Version 1.4 - 03/08/2026 : version précédente.',
            ]}
          />
        </Section>

        <Text testID="privacy-version" style={styles.version}>
          Version 1.12 - En vigueur au 28/10/2026
        </Text>
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.grey50 },
  content: { padding: screenPadding, gap: spacing.lg, paddingBottom: spacing.xxl },
  // Écran large : colonne de lecture centrée, pas une ligne de texte de 1200 px.
  contentL: { width: '100%', maxWidth: 820, alignSelf: 'center', padding: spacing.xl, paddingBottom: spacing.xxl },
  docTitle: { fontSize: typography.h2.size, fontWeight: typography.h2.weight, color: colors.night },
  docSubtitle: { fontSize: typography.small.size, color: colors.grey500 },
  section: { gap: spacing.sm },
  sectionTitle: { fontSize: typography.h3.size, fontWeight: typography.h3.weight, color: colors.grey900 },
  subTitle: { fontSize: typography.bodyMd.size, fontWeight: '700', color: colors.grey900, marginTop: spacing.xs },
  paragraph: { fontSize: typography.body.size, lineHeight: typography.body.lineHeight, color: colors.grey700 },
  bullets: { gap: spacing.xs },
  bulletRow: { flexDirection: 'row', gap: spacing.sm },
  bulletDot: { fontSize: typography.body.size, lineHeight: typography.body.lineHeight, color: colors.grey500 },
  bulletText: { flex: 1, fontSize: typography.body.size, lineHeight: typography.body.lineHeight, color: colors.grey700 },
  defItem: { gap: 2, marginTop: spacing.xs },
  defTitle: { fontSize: typography.bodyMd.size, fontWeight: '700', color: colors.grey900 },
  defBody: { fontSize: typography.body.size, lineHeight: typography.body.lineHeight, color: colors.grey700 },
  link: { color: colors.electric, fontWeight: '700' },
  version: { fontSize: typography.small.size, color: colors.grey500, marginTop: spacing.md },
});
